import { route } from './actionRouter';
import { createDedupe } from './dedupe';
import * as eventQueue from './eventQueue';
import { parsePayload } from './payloadParser';
import * as serviceWorker from './serviceWorker';
import * as sessions from './sessionTracker';

/**
 * FCM delivery handlers on the page, active only when the brand delivers with FCM. The push worker
 * (`public/firebase-messaging-sw.js`) owns everything that happens without a visible tab: it shows
 * the notification, reports `client_received` / `displayed` / `opened_direct` with the receipt
 * token, and opens or focuses the site on a click. The page covers the rest:
 *
 *   - a push while a tab is visible (the worker hands it over instead of showing it): reported as
 *     `client_received`; `onReceived` refreshes the unread count, and `displayed` is reported only
 *     if it says it showed something
 *   - a click the worker handed over (message, or the launch URL of a new tab): the direct
 *     session, the action through the site's router, then `read`
 *
 * Display and opens are deduplicated by `delivery_id`; navigation waits for the router and the
 * session (the site's router queues).
 */
const state = {
  started: false,
  unsubscribe: [],
  config: null,
  handlePushNotification: null,
  receiveDedupe: createDedupe(),
  openDedupe: createDedupe(),
  onReceived: null,
  onRead: null,
};

const log = (...args) => console.log('[NotificationsV2][handlers]', ...args);

const record = (type, notification, extra = {}) =>
  eventQueue
    .enqueue({
      event_type: type,
      notification_id: notification.notification_id,
      delivery_id: notification.delivery_id,
      receipt_token: notification.receipt_token,
      session_id: sessions.getSessionId() || undefined,
      ...extra,
    })
    .then(() => eventQueue.flush())
    .catch(err => log('cannot record', type, err?.message));

/** A message reached the page (a tab was visible). `message` is `{ data, notification }`. */
export const onMessageReceived = async (
  message,
  { foreground = false } = {},
) => {
  const notification = parsePayload(message);
  if (!notification || notification.version !== '2') {
    return { handled: false };
  }
  if (!state.receiveDedupe.first(notification.delivery_id)) {
    return { handled: false, duplicate: true };
  }

  await record('client_received', notification, {
    metadata: {
      app_state: foreground ? 'foreground' : 'background',
    },
  });

  if (foreground) {
    // No system notification for a visible tab; `displayed` only if the site showed it some way.
    // `onReceived` answers false when the site could not show it, and then nothing was displayed.
    const shown = state.onReceived
      ? state.onReceived(notification, message) !== false
      : false;
    if (shown) {
      await record('displayed', notification, {
        metadata: { surface: 'in_app' },
      });
    }
  }

  return { handled: true };
};

/**
 * A click on a notification: at most one direct open per delivery, then the action. The worker
 * already reported `opened_direct` for the clicks it hands over (`reported`), so the page does not
 * report it twice.
 */
export const onNotificationTapped = async (
  message,
  { reported = false } = {},
) => {
  const notification = parsePayload(message);
  if (!notification) {
    return { handled: false };
  }

  if (notification.version === '2') {
    if (!state.openDedupe.first(notification.delivery_id)) {
      return { handled: false, duplicate: true };
    }

    // The session this click starts is direct.
    sessions.startDirect(notification.delivery_id);
    if (!reported) {
      await record('opened_direct', notification, {
        metadata: { launch_type: 'notification_direct' },
      });
    }
  }

  const routed = await route(
    notification,
    state.config || {},
    state.handlePushNotification,
  );

  if (routed.routed && notification.version === '2') {
    await record('read', notification, { metadata: { via: 'push' } });

    // One fewer unread, as far as this page knows. The event above rides the queue and may reach
    // the server seconds later, so the site cannot ask for the new count here and get it.
    if (state.onRead) {
      state.onRead(notification);
    }
  }

  return { handled: true, routed: routed.routed };
};

/**
 * Registers the listeners once. `config` is the public config; `handlePushNotification` the site's
 * router. `onReceived` lets the site react to a foreground push (refresh the unread count),
 * answering false when it showed nothing, and `onRead` react to a click that just marked one read.
 */
export const start = async ({
  config,
  handlePushNotification,
  onReceived,
  onRead,
} = {}) => {
  state.config = config || state.config;
  state.handlePushNotification =
    handlePushNotification || state.handlePushNotification;
  state.onReceived = onReceived || state.onReceived;
  state.onRead = onRead || state.onRead;

  if (state.started) {
    return;
  }
  state.started = true;

  try {
    state.unsubscribe.push(
      serviceWorker.onMessage(serviceWorker.MESSAGES.RECEIVED, message =>
        onMessageReceived(message, { foreground: true }),
      ),
    );
    state.unsubscribe.push(
      serviceWorker.onMessage(serviceWorker.MESSAGES.OPENED, message =>
        onNotificationTapped(message, { reported: true }),
      ),
    );

    const launch = serviceWorker.takeLaunch();
    if (launch) {
      await onNotificationTapped({ data: launch }, { reported: true });
    }
  } catch (err) {
    log('cannot register worker listeners', err?.message);
  }

  log('started');
};

export const stop = () => {
  state.unsubscribe.forEach(u => {
    try {
      u?.();
    } catch {
      /* already gone */
    }
  });
  state.unsubscribe = [];
  state.started = false;
  state.receiveDedupe.clear();
  state.openDedupe.clear();
};
